import { useEffect } from 'react';
import useAppModal from 'hooks/useAppModal';
import MultiplesJobsModal, {
  JobWithService,
} from 'pages/stackedCalendar/multiplesJobModal/MultiplesJobsModal';
import { useTranslation } from 'react-i18next';
import useFetchServices from 'pages/stackedCalendar/useFetchServices';
import JobModal from 'pages/stackedCalendar/jobModal/JobModal';
import { JobLeadParams } from 'models/Jobs';
import useAppEvent from 'hooks/useAppEvent';
import { AppEventType } from 'models/Events';
import { JobsMap } from 'pages/stackedCalendar/Calendar.types';
import { Property } from 'models/Properties';
import useFetchAllProperties from 'components/domain/property/useFetchAllProperties';
import {
  propertyCalendarConditionGraphQLQuery,
  propertyCalendarGraphQLQuery,
} from 'pages/stackedCalendar/StackedCalendar.utils';

interface CreateJobModal {
  propertyUid: string;
  jobDate: Date;
}

interface UseOpenJobModal {
  jobsMap?: JobsMap;
  properties?: Property[];
  // False lets JobModal self-fetch on open instead of fetching at this hook's mount.
  fetchPropertiesWhenEmpty?: boolean;
}

const useOpenJobModal = ({
  jobsMap,
  properties,
  fetchPropertiesWhenEmpty = true,
}: UseOpenJobModal = {}) => {
  const { t } = useTranslation();
  const { openModal } = useAppModal();
  const { services } = useFetchServices();
  const { subscribe, unsubscribe } = useAppEvent();
  const { allProperties } = useFetchAllProperties({
    enabled: fetchPropertiesWhenEmpty && !properties?.length,
    conditions: propertyCalendarConditionGraphQLQuery,
    fields: propertyCalendarGraphQLQuery,
  });

  const jobsProperties = properties?.length ? properties : allProperties;

  const createJobModal = (leadParams: JobLeadParams) => {
    const JobModalId = 'job-modal';

    return openModal({
      customContent: (
        <JobModal
          modalId={JobModalId}
          properties={jobsProperties}
          // @ts-expect-error TS2322 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
          services={services}
          leadParams={leadParams}
        />
      ),
      id: JobModalId,
    });
  };

  const updateJobModal = (jobsUids: string[]) => {
    const multiplesJobsModalId = 'multiples-jobs-modal';
    const JobModalId = 'job-modal';

    // @ts-expect-error TS18048 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
    const currentsJobs = jobsUids.map((jobUid) => jobsMap[jobUid]);

    const currentJobsWithServiceName = currentsJobs.map((currentJob) => {
      return {
        ...currentJob,
        serviceTitle: services?.find(
          (service) => service?.uid === currentJob?.serviceUid,
        )?.title,
      };
    });

    const openEditJobModal = (job: JobWithService) => {
      return openModal({
        customContent: (
          <JobModal
            modalId={JobModalId}
            job={job}
            properties={jobsProperties}
            // @ts-expect-error TS2322 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
            services={services}
          />
        ),
        id: JobModalId,
      });
    };

    if (jobsUids.length === 1) {
      // @ts-expect-error TS2345 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
      openEditJobModal(currentJobsWithServiceName[0]);
      return;
    }

    const openJob = (selectedJob: JobWithService) => {
      openEditJobModal(selectedJob);
    };

    openModal({
      customContent: (
        <MultiplesJobsModal
          modalId={multiplesJobsModalId}
          // @ts-expect-error TS2322 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
          jobs={currentJobsWithServiceName}
          onConfirm={openJob}
        />
      ),
      id: multiplesJobsModalId,
      title: t('pageCalendar.stacked.selectAJob'),
    });
  };

  useEffect(() => {
    const openJobModalListener = ({ detail }: CustomEvent<CreateJobModal>) => {
      createJobModal(detail);
    };

    // @ts-expect-error TS2345 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
    subscribe(AppEventType.OPEN_CREATE_JOB_MODAL, openJobModalListener);
    return () => {
      // @ts-expect-error TS2345 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
      unsubscribe(AppEventType.OPEN_CREATE_JOB_MODAL, openJobModalListener);
    };
  }, [createJobModal]);

  return { createJobModal, updateJobModal };
};

export default useOpenJobModal;
